import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkle, CheckCircle, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'nova_festival_draft';

const FESTIVAL_OPTIONS = ['Onam', 'Diwali', 'Pongal', 'Sankranti', 'Christmas', 'Eid', 'Other'];
const MEAL_TYPE_OPTIONS = ['Breakfast', 'Lunch', 'Dinner', 'Full Day'];

const emptyForm = {
  name: '',
  organisation: '',
  mobile: '',
  email: '',
  festival: '',
  event_date: '',
  people_count: '',
  delivery_location: '',
  meal_type: '',
  special_requirements: '',
};

export const FestivalEnquiryForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setForm({ ...emptyForm, ...JSON.parse(raw) });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!submitted) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    }
  }, [form, submitted]);

  const update = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^[\d +()-]{7,20}$/.test(form.mobile)) e.mobile = 'Enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.festival) e.festival = 'Please select a festival';
    if (!form.event_date) e.event_date = 'Please select the event date';
    const count = parseInt(form.people_count, 10);
    if (!count || count < 1) e.people_count = 'Enter number of people';
    if (!form.delivery_location.trim()) e.delivery_location = 'Required';
    if (!form.meal_type) e.meal_type = 'Please select';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = { ...form, people_count: parseInt(form.people_count, 10) };
      const resp = await axios.post(`${BACKEND_URL}/api/catering/festival-enquiry`, payload);
      if (resp.data?.request_id) {
        setSubmitted(true);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        toast.success('Festival enquiry submitted successfully.');
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="festival-enquiry"
      className="relative px-6 py-16 md:py-20 bg-gradient-to-br from-[#FFFBEB] via-[#FEF3C7] to-[#FFFBEB]"
      data-testid="festival-section"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#F59E0B]/30 text-[#B45309] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-4 shadow-sm">
            <Sparkle size={14} weight="fill" /> Festival Specials
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading mb-3">
            Plan Your Festival Catering
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto">
            Share your festive occasion details. Our team will share the relevant menu, brochure and pricing.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white border border-stone-200/80 rounded-3xl p-6 md:p-10 shadow-lg"
        >
          {submitted ? (
            <div className="text-center py-8" data-testid="festival-success">
              <div className="w-16 h-16 rounded-full bg-[#F59E0B]/15 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={36} weight="duotone" className="text-[#B45309]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#1C1917] font-heading mb-3">Thank you!</h3>
              <p className="text-stone-600 max-w-lg mx-auto">
                Your festival catering request has been received. Our catering team will review your requirements and share the relevant menu, brochure and pricing options.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm(emptyForm); }}
                className="mt-6 text-[#B45309] font-semibold hover:underline"
                data-testid="festival-submit-another"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="festival-enquiry-form" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name *" error={errors.name}>
                  <input type="text" value={form.name} onChange={update('name')} className={inputCls(errors.name)} data-testid="fest-name" />
                </Field>
                <Field label="Company / Organisation" error={errors.organisation}>
                  <input type="text" value={form.organisation} onChange={update('organisation')} className={inputCls(errors.organisation)} data-testid="fest-org" />
                </Field>
                <Field label="Mobile Number *" error={errors.mobile}>
                  <input type="tel" value={form.mobile} onChange={update('mobile')} className={inputCls(errors.mobile)} data-testid="fest-mobile" placeholder="+91 98xxxxxxxx" />
                </Field>
                <Field label="Email *" error={errors.email}>
                  <input type="email" value={form.email} onChange={update('email')} className={inputCls(errors.email)} data-testid="fest-email" />
                </Field>
                <Field label="Festival / Occasion *" error={errors.festival}>
                  <select value={form.festival} onChange={update('festival')} className={inputCls(errors.festival)} data-testid="fest-select">
                    <option value="">Select…</option>
                    {FESTIVAL_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Event Date *" error={errors.event_date}>
                  <input type="date" value={form.event_date} onChange={update('event_date')} className={inputCls(errors.event_date)} data-testid="fest-date" />
                </Field>
                <Field label="Number of People *" error={errors.people_count}>
                  <input type="number" min="1" value={form.people_count} onChange={update('people_count')} className={inputCls(errors.people_count)} data-testid="fest-count" />
                </Field>
                <Field label="Meal Type *" error={errors.meal_type}>
                  <select value={form.meal_type} onChange={update('meal_type')} className={inputCls(errors.meal_type)} data-testid="fest-meal-type">
                    <option value="">Select…</option>
                    {MEAL_TYPE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Delivery Location *" error={errors.delivery_location} colSpan>
                  <input type="text" value={form.delivery_location} onChange={update('delivery_location')} className={inputCls(errors.delivery_location)} data-testid="fest-location" placeholder="Full address or city / area" />
                </Field>
              </div>
              <Field label="Special Requirements" error={errors.special_requirements}>
                <textarea rows="4" value={form.special_requirements} onChange={update('special_requirements')} className={inputCls(errors.special_requirements)} data-testid="fest-special" placeholder="Regional menu preference, dietary restrictions, presentation style, etc." />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white font-semibold text-base px-6 py-4 rounded-full transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="fest-submit"
              >
                {submitting ? (
                  <>
                    <Spinner size={20} weight="bold" className="animate-spin" /> Submitting…
                  </>
                ) : (
                  <>Request Festival Menu →</>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const inputCls = (err) => `w-full px-4 py-3 border rounded-xl bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/40 transition-all ${err ? 'border-red-500' : 'border-stone-300'}`;

const Field = ({ label, error, children, colSpan }) => (
  <div className={colSpan ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-semibold text-stone-700 mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600" data-testid="field-error">{error}</p>}
  </div>
);

export default FestivalEnquiryForm;
